import React, { useEffect, useState } from 'react';
import { Alert } from 'react-bootstrap';
import moment from 'moment';
import * as api from '~/Services/Service';
import { strParaMonetario } from '~/Utils/Helpers';

const formatarTexto = valor => {
  if (valor !== null && valor !== undefined && valor !== '') {
    return valor;
  }

  return '-';
};

const formatarData = data => {
  if (!data) {
    return '-';
  }

  const dataFormatada = moment(data);
  if (!dataFormatada.isValid() || dataFormatada.year() < 1901) {
    return '-';
  }

  return dataFormatada.format('DD/MM/YYYY');
};

const formatarMoeda = valor => strParaMonetario(valor, false);

const formatarNumero = valor => {
  if (valor !== null && valor !== undefined && valor !== '') {
    return valor;
  }

  return 0;
};

const formatarSituacaoCliente = inadimplente => {
  if (inadimplente === 'S') {
    return 'Inadimplente';
  }

  if (inadimplente === 'N') {
    return 'Adimplente';
  }

  return '-';
};

const existeValorBancario = resumo => {
  if (!resumo) {
    return false;
  }

  return (
    resumo.conta_banco ||
    resumo.conta_agencia ||
    resumo.conta_agencia_dig ||
    resumo.conta_conta ||
    resumo.conta_conta_dig ||
    resumo.conta_favorecido ||
    resumo.conta_cnpj ||
    resumo.tipo_conta ||
    resumo.tipo_conta_descricao
  );
};

const montarDescricaoConta = resumo => {
  const agencia = [
    resumo.conta_agencia,
    resumo.conta_agencia_dig,
  ].filter(valor => valor).join('-');
  const conta = [
    resumo.conta_conta,
    resumo.conta_conta_dig,
  ].filter(valor => valor).join('-');

  if (agencia && conta) {
    return `${agencia} / ${conta}`;
  }

  if (agencia) {
    return agencia;
  }

  return conta;
};

const montarCodigoConta = resumo => {
  if (resumo.codigo_grupo_empresa && resumo.codigo_conta) {
    return `${resumo.codigo_grupo_empresa}/${resumo.codigo_conta}`;
  }

  return '-';
};

const SecaoResumo = ({ titulo, linhas }) => (
  <section className="ResumoRescisao__secao">
    <h5>{titulo}</h5>
    <div className="ResumoRescisao__grid">
      {linhas.map(linha => (
        <div className="ResumoRescisao__campo" key={linha.label}>
          <span>{linha.label}</span>
          <strong>{linha.valor}</strong>
        </div>
      ))}
    </div>
  </section>
);

const RetencoesResumo = ({ retencoes }) => {
  if (!retencoes || retencoes.length === 0) {
    return null;
  }

  return (
    <section className="ResumoRescisao__secao">
      <h5>Retenções</h5>
      <div className="ResumoRescisao__tabela">
        <div className="ResumoRescisao__linha ResumoRescisao__linha--cabecalho">
          <span>Seq.</span>
          <span>Descrição</span>
          <span>Base</span>
          <span>Fator</span>
          <span>Tipo fator</span>
          <span>Tipo base</span>
          <span>Valor</span>
        </div>
        {retencoes.map((retencao, index) => (
          <div className="ResumoRescisao__linha" key={index}>
            <span>{formatarTexto(retencao.sequencia)}</span>
            <span>{formatarTexto(retencao.descricao)}</span>
            <span>{formatarMoeda(retencao.base_calculo)}</span>
            <span>{formatarTexto(retencao.fator)}</span>
            <span>{formatarTexto(retencao.tipo_fator)}</span>
            <span>{formatarTexto(retencao.tipo_base_calculo)}</span>
            <span>{formatarMoeda(retencao.valor_retencao)}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

const ResumoItem = ({ resumo }) => {
  const identificacao = [
    { label: 'Boleto', valor: formatarTexto(resumo.boleto) },
    { label: 'Conta', valor: montarCodigoConta(resumo) },
    { label: 'Cliente', valor: formatarTexto(resumo.nome_cliente) },
    { label: 'Apelido da conta', valor: formatarTexto(resumo.apelido_conta) },
    { label: 'Família', valor: formatarTexto(resumo.nome_da_familia) },
    { label: 'Código família', valor: formatarTexto(resumo.codigo_familia) },
    { label: 'Corretor', valor: formatarTexto(resumo.nome_corretor) },
  ];

  const valoresPagos = [
    { label: 'Valor total pago', valor: formatarMoeda(resumo.tot_pgto) },
    { label: 'Cessões anteriores', valor: formatarMoeda(resumo.tot_pgto_ant) },
    { label: 'Proprietário atual', valor: formatarMoeda(resumo.tot_pgto) },
    { label: 'Parcelas pagas', valor: formatarNumero(resumo.parc_pgto) },
    { label: 'Parcelas anteriores', valor: formatarNumero(resumo.parc_pgto_ant) },
  ];

  const debitosEncargos = [
    { label: 'IPTU em atraso', valor: formatarMoeda(resumo.iptu) },
    { label: 'Taxa de limpeza', valor: formatarMoeda(resumo.taxa_limpeza) },
    { label: 'Água em atraso', valor: formatarMoeda(resumo.agua) },
    { label: 'Condomínio em atraso', valor: formatarMoeda(resumo.condominio) },
    { label: 'Taxa iluminação pública', valor: formatarMoeda(resumo.tx_iluminacao_publica) },
    { label: 'Multa de postura', valor: formatarMoeda(resumo.multa_postura) },
    { label: 'Alvara de construção', valor: formatarMoeda(resumo.alvara_construcao) },
    { label: 'Averbação matrícula', valor: formatarMoeda(resumo.averbacao) },
    { label: 'Custas judiciais', valor: formatarMoeda(resumo.custa_judicial) },
    { label: 'Notificações judiciais', valor: formatarMoeda(resumo.custa_notifi) },
    { label: 'Custas processuais', valor: formatarMoeda(resumo.custa_proc) },
    { label: 'Honorários advocáticos', valor: formatarMoeda(resumo.custa_hono) },
    { label: 'Valor de fruição', valor: formatarMoeda(resumo.frui_vr) },
  ];

  const resumoRescisao = [
    { label: 'Despesas administrativas', valor: formatarMoeda(resumo.despesas_administrativas) },
    { label: 'Total a reembolsar', valor: formatarMoeda(resumo.tot_reembolsar) },
    { label: 'Reembolso em parcelas', valor: formatarMoeda(resumo.reembolso_parcelas) },
    { label: 'Reembolso a vista', valor: formatarMoeda(resumo.reembolso_a_vista) },
    { label: 'Valor calculado fechado', valor: formatarMoeda(resumo.valor) },
    { label: 'Valor de quitação', valor: formatarMoeda(resumo.valor_quitacao) },
    { label: 'Valor atual do lote', valor: formatarMoeda(resumo.valor_lote) },
    { label: 'Taxa', valor: formatarMoeda(resumo.taxa) },
    { label: 'Quantidade de parcelas', valor: formatarNumero(resumo.quantidade_parcelas) },
  ];

  const parcelasAberto = [
    { label: 'Ultima parcela', valor: formatarData(resumo.ultima_parcela) },
    { label: 'Qtd vencidas', valor: formatarNumero(resumo.qtd_vencidas) },
    { label: 'Valor vencidas', valor: formatarMoeda(resumo.valor_vencidas) },
    { label: 'Qtd a vencer', valor: formatarNumero(resumo.qtd_vencer) },
    { label: 'Valor a vencer', valor: formatarMoeda(resumo.valor_vencer) },
    { label: 'Qtd imobiliaria', valor: formatarNumero(resumo.qtd_imob) },
    { label: 'Valor imobiliaria', valor: formatarMoeda(resumo.valor_imob) },
    { label: 'Qtd incorporadora', valor: formatarNumero(resumo.qtd_incorp) },
    { label: 'Valor incorporadora', valor: formatarMoeda(resumo.valor_incorp) },
  ];

  const contratoDatas = [
    { label: 'Tipo lote', valor: formatarTexto(resumo.tipo_lote) },
    { label: 'Situação cliente', valor: formatarSituacaoCliente(resumo.inadimplente) },
    { label: 'Data da venda', valor: formatarData(resumo.data_venda) },
    { label: 'Data do contrato', valor: formatarData(resumo.dt_contrato) },
    { label: 'Data da rescisão', valor: formatarData(resumo.data_rescisao) },
    { label: 'Data efetivação', valor: formatarData(resumo.data_efetivacao || resumo.data) },
    { label: 'Data lançamento', valor: formatarData(resumo.data_lancamento) },
    { label: 'Gravação', valor: formatarTexto(resumo.login_gravou) },
    { label: 'Efetivação', valor: formatarTexto(resumo.login_efetivou) },
    { label: 'Lançamento', valor: formatarTexto(resumo.login_lancou) },
    { label: 'Motivo', valor: formatarTexto(resumo.motivo_descricao || resumo.motivo) },
    { label: 'Submotivo', valor: formatarTexto(resumo.sub_motivo_descricao || resumo.sub_motivo || resumo.submotivo) },
    { label: 'Modo pagamento', valor: formatarTexto(resumo.modo_pg_descricao || resumo.modo_pg) },
    { label: 'Tipo', valor: formatarTexto(resumo.tipo_descricao || resumo.tipo) },
    { label: 'Demanda', valor: formatarTexto(resumo.demanda) },
    { label: 'Benfeitoria', valor: formatarTexto(resumo.benfeitoria) },
  ];

  const dadosBancarios = [
    { label: 'Banco', valor: formatarTexto(resumo.conta_banco) },
    { label: 'Agência / conta', valor: formatarTexto(montarDescricaoConta(resumo)) },
    { label: 'Favorecido', valor: formatarTexto(resumo.conta_favorecido) },
    { label: 'CPF/CNPJ favorecido', valor: formatarTexto(resumo.conta_cnpj) },
    { label: 'Tipo de conta', valor: formatarTexto(resumo.tipo_conta_descricao || resumo.tipo_conta) },
  ];

  return (
    <div className="ResumoRescisao__item">
      <div className="ResumoRescisao__cabecalho">
        <h4>Cálculo de rescisão</h4>
        <span>Boleto {formatarTexto(resumo.boleto)}</span>
      </div>

      <SecaoResumo titulo="Identificacao" linhas={identificacao} />
      <SecaoResumo titulo="Valores pagos" linhas={valoresPagos} />
      <SecaoResumo titulo="Debitos e encargos" linhas={debitosEncargos} />
      <RetencoesResumo retencoes={resumo.retencoes} />
      <SecaoResumo titulo="Resumo da rescisao" linhas={resumoRescisao} />
      <SecaoResumo titulo="Parcelas em aberto" linhas={parcelasAberto} />
      <SecaoResumo titulo="Contrato, datas e usuarios" linhas={contratoDatas} />
      {existeValorBancario(resumo) && (
        <SecaoResumo titulo="Dados bancarios" linhas={dadosBancarios} />
      )}
      {resumo.obs && (
        <section className="ResumoRescisao__secao">
          <h5>Observação</h5>
          <p className="ResumoRescisao__observacao">{resumo.obs}</p>
        </section>
      )}
    </div>
  );
};

const ResumoRescisao = ({ cpf }) => {
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(false);
  const [resumos, setResumos] = useState([]);

  useEffect(() => {
    if (!cpf) {
      setResumos([]);
      return;
    }

    setCarregando(true);
    setErro(false);

    api.buscarResumoRescisaoProspecto(cpf)
      .then(response => {
        if (Array.isArray(response)) {
          setResumos(response);
        } else if (response) {
          setResumos([response]);
        } else {
          setResumos([]);
        }
        setCarregando(false);
      })
      .catch(() => {
        setErro(true);
        setCarregando(false);
      });
  }, [cpf]);

  if (carregando) {
    return (
      <div className="ResumoRescisao">
        <Alert variant="info" className="mb-0">
          Carregando resumo de rescisão...
        </Alert>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="ResumoRescisao">
        <Alert variant="danger" className="mb-0">
          Erro ao carregar resumo de rescisão.
        </Alert>
      </div>
    );
  }

  if (!resumos || resumos.length === 0) {
    return (
      <div className="ResumoRescisao">
        <Alert variant="warning" className="mb-0">
          Nenhum resumo de rescisão encontrado.
        </Alert>
      </div>
    );
  }

  return (
    <div className="ResumoRescisao">
      {resumos.map((resumo, index) => (
        <ResumoItem resumo={resumo} key={resumo.boleto || index} />
      ))}
    </div>
  );
};

export default ResumoRescisao;
